/** Author profile — works for both members and ingested attributions (see services/authors.ts). */

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { PromptCard as PromptCardDto } from '@promptvault/shared';
import { fetchAuthor, fetchAuthorPrompts } from '../api/community';
import { PromptCard } from '../components/PromptCard';
import { Padded, Screen } from '../components/Screen';
import { ErrorState, Placeholder, SkeletonGrid } from '../components/feedback';
import { Avatar } from '../components/primitives';
import { MEANINGFUL_COUNT, MEANINGFUL_RATINGS } from '../components/SourceStats';
import { Icon } from '../components/Icon';
import { compactNumber, decimal1 } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';

export function Author() {
  const { key = '' } = useParams<{ key: string }>();
  const navigate = useNavigate();
  const { t, lang } = useI18n();

  const author = useQuery({
    queryKey: ['author', key],
    queryFn: () => fetchAuthor(key),
    enabled: Boolean(key),
  });

  const prompts = useInfiniteQuery({
    queryKey: ['author-prompts', key],
    queryFn: ({ pageParam }) => fetchAuthorPrompts(key, pageParam as string | undefined),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: Boolean(key),
  });

  const items = useMemo(() => prompts.data?.pages.flatMap((p) => p.items) ?? [], [prompts.data]);
  const openCard = useCallback((card: PromptCardDto) => navigate(`/prompt/${card.id}`), [navigate]);

  const sentinel = useInfiniteScroll(() => void prompts.fetchNextPage(), {
    enabled: Boolean(prompts.hasNextPage),
    loading: prompts.isFetchingNextPage,
  });

  if (author.isError) {
    return (
      <Screen>
        <ErrorState
          message={(author.error as Error)?.message}
          onRetry={() => void author.refetch()}
        />
      </Screen>
    );
  }

  const a = author.data;

  return (
    <Screen chrome="surface">
      <div className="hairline-b flex flex-col gap-3 bg-surface px-gutter pb-4 pt-4.5">
        <div className="flex items-center gap-3">
          <Avatar name={a?.name ?? '…'} photoUrl={a?.photoUrl} seed={key} size={64} />
          <div className="min-w-0 flex-1">
            <p className="bidi-plaintext truncate text-title font-semibold text-text">
              {a?.name ?? '…'}
            </p>
            {a?.username && (
              <p className="bidi-plaintext truncate text-subhead text-hint">@{a.username}</p>
            )}
            {a && !a.isMember && <p className="text-subhead text-hint">{t.sourceMeta}</p>}
          </div>
        </div>
        {/*
          A zero is a review; an absent number reads as new (D48-first).

          This drew `★ 0.0` and `0 downloads` on every author page in the library, unconditionally
          — the exact defect the cards and the detail screen were fixed for, on a surface nobody
          revisited afterwards. The thresholds are the shared ones, so all three surfaces agree
          about when our own counters have earned a place on screen. `promptCount` always shows:
          it is a fact about the library rather than a verdict on the work, and it is never zero
          on a page that exists.
        */}
        {a && (
          <div className="tnum flex flex-wrap items-center gap-3 text-footnote text-hint">
            <span>
              <b className="font-semibold text-text">{compactNumber(a.promptCount, lang)}</b>{' '}
              {t.authorPrompts}
            </span>
            {a.downloadCount >= MEANINGFUL_COUNT && (
              <>
                <span aria-hidden="true" className="opacity-45">
                  ·
                </span>
                <span>
                  <b className="font-semibold text-text">{compactNumber(a.downloadCount, lang)}</b>{' '}
                  {t.authorDownloads}
                </span>
              </>
            )}
            {a.ratingCount >= MEANINGFUL_RATINGS && (
              <>
                <span aria-hidden="true" className="opacity-45">
                  ·
                </span>
                <span className="flex items-center gap-1">
                  <Icon name="star" size={13} fill className="text-star" />
                  {decimal1(a.ratingAvg, lang)}
                </span>
              </>
            )}
          </div>
        )}
      </div>

      {prompts.isLoading ? (
        <Padded className="pt-3.5">
          <SkeletonGrid count={4} />
        </Padded>
      ) : items.length === 0 ? (
        <Placeholder icon="search" title={t.noResults} description={t.noResultsSub} />
      ) : (
        <Padded className="pt-3.5">
          <div className="grid grid-cols-2 gap-2">
            {items.map((card, i) => (
              <PromptCard key={card.id} card={card} onOpen={openCard} priority={i < 4} />
            ))}
          </div>
          <div ref={sentinel} aria-hidden="true" className="h-1" />
        </Padded>
      )}

      <div className="h-6" />
    </Screen>
  );
}
